import React, { useEffect, useState, useRef } from 'react';
import { motion, useScroll, useTransform, useMotionValue, useSpring } from 'framer-motion';
import { ArrowRight, ChevronDown } from 'lucide-react';
export function Hero() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [mousePosition, setMousePosition] = useState({
    x: 0.5,
    y: 0.5
  });
  const {
    scrollYProgress
  } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start']
  });
  // Smooth spring animation for mouse tracking
  const springConfig = {
    damping: 25,
    stiffness: 150
  };
  const mouseX = useSpring(useMotionValue(0.5), springConfig);
  const mouseY = useSpring(useMotionValue(0.5), springConfig);
  // Handle mouse movement for sunlight effect
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    mouseX.set(x);
    mouseY.set(y);
    setMousePosition({
      x,
      y
    });
  };
  // Apple-style parallax transforms
  const headlineOpacity = useTransform(scrollYProgress, [0, 0.4], [1, 0]);
  const headlineY = useTransform(scrollYProgress, [0, 0.5], ['0%', '-20%']);
  const subheadlineOpacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const ctaOpacity = useTransform(scrollYProgress, [0, 0.25], [1, 0]);
  // Video effects on scroll
  const videoScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const videoBlur = useTransform(scrollYProgress, [0, 0.6], [0, 8]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 0.5], [0.4, 0.7]);
  // Scroll indicator
  const scrollIndicatorOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0]);
  // Calculate sunlight gradient position
  const sunlightX = mousePosition.x * 100;
  const sunlightY = mousePosition.y * 100;
  return <div ref={containerRef} onMouseMove={handleMouseMove} className="relative h-[120vh] w-full overflow-hidden bg-[#1A1A1A]" data-id="element-119">
      {/* Cinematic Video Background - Mountain Home at Sunset */}
      <motion.div style={{
      scale: videoScale
    }} className="absolute inset-0 w-full h-full" data-id="element-120">
        <motion.div style={{
        filter: useTransform(videoBlur, v => `blur(${v}px)`)
      }} className="w-full h-full" data-id="element-121">
          <img src="https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=1920&q=80" alt="Mountain modern custom home nestled in Idaho forest at dusk" className="w-full h-full object-cover" data-id="element-122" />
        </motion.div>

        {/* Warm sunset overlay */}
        <motion.div style={{
        opacity: overlayOpacity
      }} className="absolute inset-0 bg-gradient-to-b from-[#1A1A1A]/70 via-[#1A1A1A]/30 to-[#1A1A1A]/80" data-id="element-123" />

        {/* Golden hour color grade */}
        <div className="absolute inset-0 bg-gradient-to-tr from-[#A07656]/10 via-transparent to-[#D4A574]/5 mix-blend-overlay" data-id="element-124" />
      </motion.div>

      {/* Dappled sunlight overlay - follows mouse */}
      <motion.div className="absolute inset-0 z-10 pointer-events-none" style={{
      background: `radial-gradient(ellipse 60% 40% at ${sunlightX}% ${sunlightY}%, rgba(212, 165, 116, 0.15) 0%, transparent 50%)`
    }} data-id="element-125" />

      {/* Pine tree shadow pattern overlay */}
      <div className="absolute inset-0 z-10 pointer-events-none opacity-[0.03]" data-id="element-126">
        <div className="absolute inset-0" style={{
        backgroundImage: `
              repeating-linear-gradient(
                ${75 + (mousePosition.x - 0.5) * 10}deg,
                transparent,
                transparent 30px,
                rgba(26, 26, 26, 0.8) 30px,
                rgba(26, 26, 26, 0.8) 35px,
                transparent 35px,
                transparent 80px
              )
            `
      }} data-id="element-127" />
      </div>

      {/* Content - Sticky within viewport */}
      <div className="sticky top-0 h-screen flex flex-col justify-center items-center text-center px-4 sm:px-6 lg:px-8" data-id="element-128">
        <motion.div style={{
        opacity: headlineOpacity,
        y: headlineY
      }} className="max-w-6xl mx-auto relative z-20" data-id="element-129">
          {/* Centered Logo */}
          <motion.img src="https://cdn.magicpatterns.com/uploads/rsVSuidRepyz9oxNYVg3rK/Iron_Ridge_Logo.png" alt="Iron Ridge Custom Homes" initial={{
          opacity: 0,
          y: 20,
          scale: 0.95
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} transition={{
          duration: 1,
          delay: 0.2,
          ease: [0.25, 0.1, 0.25, 1]
        }} className="h-28 sm:h-36 md:h-44 w-auto mx-auto mb-10 brightness-0 invert drop-shadow-[0_4px_20px_rgba(0,0,0,0.4)]" data-id="element-130" />

          {/* Main Headline with sunlight effect */}
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 0.5,
          ease: [0.25, 0.1, 0.25, 1]
        }} className="relative" data-id="element-131">
            {/* Base text */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-serif font-bold text-[#F5F3F0] leading-[1.05] mb-8 tracking-tight" data-id="element-132">
              Build Your
              <br data-id="element-133" />
              <span className="text-[#E8E4DF]" data-id="element-134">Vision</span>
              <br data-id="element-135" />
              <span className="italic font-semibold text-[#D4CFC8]" data-id="element-136">
                in the Mountains.
              </span>
            </h1>

            {/* Sunlight highlight layer - follows mouse */}
            <motion.div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true" data-id="element-137">
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-serif font-bold leading-[1.05] mb-8 tracking-tight" style={{
              background: `radial-gradient(ellipse 300px 200px at ${sunlightX}% ${sunlightY}%, rgba(212, 165, 116, 0.4) 0%, transparent 70%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }} data-id="element-138">
                Build Your
                <br data-id="element-139" />
                <span data-id="element-140">Vision</span>
                <br data-id="element-141" />
                <span className="italic font-semibold" data-id="element-142">in the Mountains.</span>
              </h1>
            </motion.div>
          </motion.div>

          {/* Sub-headline */}
          <motion.p style={{
          opacity: subheadlineOpacity
        }} initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.8
        }} className="text-base sm:text-lg md:text-xl text-[#E8E4DF]/70 max-w-2xl mx-auto mb-12 font-light leading-relaxed" data-id="element-143">
            Crafting bespoke mountain homes in Post Falls
            <br className="hidden sm:block" data-id="element-144" />
            and the greater Northern Idaho region.
          </motion.p>

          {/* CTA Button */}
          <motion.div style={{
          opacity: ctaOpacity
        }} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 1.1
        }} data-id="element-145">
            <motion.button whileHover={{
            scale: 1.03
          }} whileTap={{
            scale: 0.98
          }} className="group relative inline-flex items-center gap-3 px-10 py-5 bg-[#A07656] text-white overflow-hidden transition-all duration-300 hover:bg-[#B8896A] hover:shadow-[0_0_50px_rgba(160,118,86,0.35)]" data-id="element-146">
              {/* Shine effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" data-id="element-147" />
              <span className="relative z-10 text-sm tracking-[0.2em] uppercase font-semibold" data-id="element-148">
                Begin Your Journey
              </span>
              <ArrowRight className="w-4 h-4 relative z-10 transition-transform duration-300 group-hover:translate-x-1" data-id="element-149" />
            </motion.button>
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div style={{
        opacity: scrollIndicatorOpacity
      }} className="absolute bottom-12 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center" data-id="element-150">
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 1.5
        }} className="flex flex-col items-center gap-3" data-id="element-151">
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#F5F3F0]/40 font-medium" data-id="element-152">
              Discover
            </span>
            <motion.div animate={{
            y: [0, 8, 0]
          }} transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-153">
              <ChevronDown className="w-5 h-5 text-[#F5F3F0]/40" data-id="element-154" />
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#1A1A1A] to-transparent z-10 pointer-events-none" data-id="element-155" />

      {/* Side vignette for cinematic feel */}
      <div className="absolute inset-0 pointer-events-none z-10" data-id="element-156">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(26,26,26,0.3)_100%)]" data-id="element-157" />
      </div>
    </div>;
}